import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toaster } from 'baseui/toast';
import { imageApiHeaders, imageApiReady, imageApiUrl } from '../services/imageApi';

const POLL_INTERVAL_MS = 60_000;
const REQUEST_TIMEOUT_MS = 10_000;

export type AnalyzerStatus = 'running' | 'degraded' | 'unavailable' | 'checking';

export interface AnalyzerHealth {
  status: AnalyzerStatus;
  checkedAt: string;
  detail?: string;
}

// Probes the Image Analyzer (savePicture lambda, /image route). The backend
// exposes `action=health` (SAM-141); a 4xx still proves the service is up,
// but a 5xx is reported as degraded.
export const probeHealth = async (): Promise<AnalyzerHealth> => {
  const checkedAt = new Date().toISOString();
  // SAM-949: the probe goes through the authenticated image API. Without its base URL or a session nothing is sent and
  // the analyzer is reported unreachable, never probed at some default (the old default was the production gateway).
  if (!imageApiReady()) return { status: 'unavailable', checkedAt, detail: 'Not configured or not signed in' };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const resp = await fetch(imageApiUrl('action=health'), {
      method: 'GET',
      headers: imageApiHeaders(),
      signal: controller.signal,
    });
    if (resp.ok) {
      const body = await resp.json().catch(() => null);
      const reported = body?.status ?? body?.body?.status;
      if (reported && reported !== 'ok' && reported !== 'running') {
        return { status: 'degraded', checkedAt, detail: String(reported) };
      }
      return { status: 'running', checkedAt };
    }
    // SAM-1311: any 5xx is the service failing (502/503/504 from API Gateway, or the lambda's own 500 on the
    // health action); the badge must not say Running while the probe errors.
    if (resp.status >= 500) {
      return { status: 'degraded', checkedAt, detail: `HTTP ${resp.status}` };
    }
    // A 4xx was produced by the lambda itself, which proves the service executed and is alive.
    return { status: 'running', checkedAt, detail: `HTTP ${resp.status}` };
  } catch {
    return { status: 'unavailable', checkedAt, detail: 'No response (timeout or network error)' };
  } finally {
    clearTimeout(timer);
  }
};

export const useImageAnalyzerHealth = () => {
  const query = useQuery({
    queryKey: ['image-analyzer-health'],
    queryFn: probeHealth,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
    // Health probes must not linger as "stale but shown" — always poll fresh.
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  // Notify only on transitions so a sustained outage produces exactly one
  // alert (and one recovery notice), never a toast per poll.
  const prevStatus = useRef<AnalyzerStatus | null>(null);
  const status = query.data?.status;
  useEffect(() => {
    if (!status) return;
    const prev = prevStatus.current;
    prevStatus.current = status;
    if (prev === null || prev === status) return;
    if (status === 'unavailable' || status === 'degraded') {
      toaster.negative(
        `Image Analyzer is ${status === 'unavailable' ? 'unavailable' : 'degraded'} — document scanning may fail.`,
        { autoHideDuration: 0 },
      );
    } else if (status === 'running' && (prev === 'unavailable' || prev === 'degraded')) {
      toaster.positive('Image Analyzer recovered and is running again.', { autoHideDuration: 6000 });
    }
  }, [status]);

  return {
    status: (status ?? 'checking') as AnalyzerStatus,
    checkedAt: query.data?.checkedAt,
    detail: query.data?.detail,
  };
};
